import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, faqs } from '@/mocks/categories';

const categoryEntries = Object.entries(categories);

const stats = [
  { value: '50,000+', label: '累计用户' },
  { value: '120,000+', label: '起草文档' },
  { value: '6', label: '服务分类' },
  { value: '98.5%', label: '用户满意度' },
];

const features = [
  {
    icon: 'ri-file-text-line',
    title: '专业模板库',
    desc: '涵盖商务、法律、行政、学术、创意六大领域的专业起草模板，满足各类场景需求。',
  },
  {
    icon: 'ri-guide-line',
    title: '保姆级指南',
    desc: '每份模板配备详细的起草指南，从框架搭建到细节润色，手把手教你完成专业文件。',
  },
  {
    icon: 'ri-time-line',
    title: '高效省时',
    desc: '告别从零开始的烦恼，借助专业模板和指南，将起草效率提升 70% 以上。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '合规保障',
    desc: '所有法律相关模板由资深律师审核，行政公文遵循国家标准格式，确保内容合规。',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续更新',
    desc: '紧跟法律法规变化和行业趋势，每周更新起草指南内容，确保知识始终最新。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '贴心服务',
    desc: '无论您是新手还是专业人士，都能在这里找到适合自己水平的起草指导。',
  },
];

const testimonials = [
  {
    name: '王总',
    role: '中小企业负责人',
    text: '以前每次写合同都要找律师，成本高还费时间。用了 17.c-起草口 之后，很多常规合同我自己就能完成了，省了一大笔费用。',
    rating: 5,
  },
  {
    name: '李律师',
    role: '执业律师',
    text: '作为律师，我经常需要起草各类法律文书。17.c-起草口 的内容专业度很高，格式规范，给我提供了很好的参考框架。',
    rating: 5,
  },
  {
    name: '张同学',
    role: '硕士研究生',
    text: '写开题报告和论文的时候完全没头绪，同学推荐了 17.c-起草口，上面的学术写作指南真的帮了大忙！',
    rating: 5,
  },
];

export default function Home() {
  return (
    <>
      <SEOHead
        title="17.c-起草口 | 专业文案起草入口 - 在线文件起草与草稿撰写平台"
        description="17.c-起草口 是您高效完成文案起草的专属入口平台，提供一站式草稿撰写、内容规划与文件起草服务。涵盖商务文件、合同草案、创意文案、法律文书等专业起草服务，助您快速完成每一份草稿，提升工作效率。"
        keywords="17.c-起草口,起草口文案平台,在线文件起草,草稿撰写工具,合同起草服务,文案快速生成,专业起草入口"
        canonical="/"
        ogImage="https://readdy.ai/api/search-image?query=Professional%20minimalist%20document%20drafting%20platform%20with%20warm%20ambient%20lighting%2C%20clean%20desk%20aesthetic%2C%20pen%20and%20paper%2C%20soft%20cream%20tones%2C%20editorial%20photography%2C%20elegant%20composition%2C%20no%20people%2C%20warm%20neutral%20background&width=1200&height=630&seq=home-og-1&orientation=landscape"
      />

      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: '17.c-起草口',
          url: 'https://zhimycloud.cn',
          description: '专业的在线文案起草平台，提供一站式草稿撰写、内容规划与文件起草服务。',
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '400-888-1717',
            contactType: 'customer service',
            availableLanguage: 'Chinese',
          },
        }}
      />

      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: '17.c-起草口',
          url: 'https://zhimycloud.cn',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://zhimycloud.cn/category?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full min-h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20modern%20office%20workspace%20with%20natural%20light%2C%20elegant%20wooden%20desk%2C%20fountain%20pen%2C%20cream%20colored%20paper%2C%20soft%20ambient%20lighting%2C%20artistic%20composition%2C%20no%20people%2C%20warm%20beige%20and%20cream%20tones%2C%20editorial%20photography%20style%20with%20shallow%20depth%20of%20field&width=1600&height=900&seq=hero-bg-1&orientation=landscape"
              alt="17.c-起草口 专业文案起草平台"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/25"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center py-20">
            <div className="max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-background-50/20 backdrop-blur-sm rounded-full border border-background-50/30 mb-6">
                <i className="ri-sparkling-line text-accent-400 text-sm"></i>
                <span className="text-background-50 text-sm">专业文案起草入口平台</span>
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-6">
                高效完成每一份
                <br />
                <span className="text-accent-400">文案起草</span>
              </h1>
              <p className="text-background-50/80 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
                17.c-起草口 为您提供一站式草稿撰写、内容规划与文件起草服务。
                无论是商务文件、合同草案还是创意文案，助您快速完成，提升工作效率。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category"
                  className="px-8 py-3.5 bg-accent-500 text-background-50 rounded-md text-base font-medium hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap"
                >
                  立即开始起草
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3.5 bg-background-50/15 backdrop-blur-sm text-background-50 rounded-md text-base font-medium border border-background-50/30 hover:bg-background-50/25 transition-colors duration-200 whitespace-nowrap"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="relative -mt-12 z-20 w-full px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="bg-background-50 rounded-lg p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-foreground-950 mb-1">{stat.value}</div>
                  <div className="text-foreground-500 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="w-full px-4 md:px-6 py-20 md:py-28">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                为什么选择 17.c-起草口
              </h2>
              <p className="text-foreground-500 text-base md:text-lg max-w-2xl mx-auto">
                六大核心优势，为您的每一次起草保驾护航
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feat) => (
                <div key={feat.title} className="bg-background-100 rounded-lg p-6 hover:bg-background-200/70 transition-colors duration-200">
                  <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${feat.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <h3 className="text-foreground-950 font-semibold text-base mb-2">{feat.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="w-full px-4 md:px-6 py-20 md:py-28 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                覆盖全场景起草需求
              </h2>
              <p className="text-foreground-500 text-base md:text-lg max-w-2xl mx-auto">
                六大分类，满足从商务到个人的多样化起草需求
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryEntries.map(([slug, cat]) => (
                <Link
                  key={slug}
                  to="/category"
                  className="bg-background-50 rounded-lg p-6 hover:bg-background-100/80 transition-all duration-200 group"
                >
                  <div className="w-11 h-11 bg-accent-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-accent-200 transition-colors">
                    <i className={`${cat.icon} text-accent-600 text-lg`}></i>
                  </div>
                  <h3 className="text-foreground-950 font-semibold text-base mb-1.5">{cat.name}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-3">{cat.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-foreground-400 text-xs">{cat.count} 篇指南</span>
                    <i className="ri-arrow-right-line text-foreground-400 group-hover:text-accent-600 transition-colors"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="w-full px-4 md:px-6 py-20 md:py-28">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                三步完成专业起草
              </h2>
              <p className="text-foreground-500 text-base md:text-lg">
                简单高效，无需学习成本
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { step: '01', icon: 'ri-search-line', title: '选择分类', desc: '根据您的需求，从六大服务分类中找到对应的起草领域。' },
                { step: '02', icon: 'ri-file-copy-line', title: '查阅指南', desc: '浏览专业的起草指南，了解标准格式、必备内容和写作技巧。' },
                { step: '03', icon: 'ri-pencil-line', title: '完成起草', desc: '参考指南和模板框架，轻松完成专业规范的文案起草。' },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-16 h-16 mx-auto bg-accent-100 rounded-full flex items-center justify-center mb-5">
                    <span className="text-accent-600 font-bold text-lg">{item.step}</span>
                  </div>
                  <div className="w-12 h-12 mx-auto bg-background-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-foreground-700 text-xl`}></i>
                  </div>
                  <h3 className="text-foreground-950 font-semibold text-base mb-2">{item.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-20 md:py-28 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                用户口碑
              </h2>
              <p className="text-foreground-500 text-base md:text-lg">
                听听用户怎么说
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.name} className="bg-background-50 rounded-lg p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                    "{t.text}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-accent-100 rounded-full flex items-center justify-center">
                      <span className="text-accent-600 font-semibold text-sm">{t.name[0]}</span>
                    </div>
                    <div>
                      <div className="text-foreground-950 text-sm font-medium">{t.name}</div>
                      <div className="text-foreground-500 text-xs">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full px-4 md:px-6 py-20 md:py-28">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                常见问题
              </h2>
              <p className="text-foreground-500 text-base md:text-lg">
                关于 17.c-起草口，您可能想了解
              </p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <details key={i} className="bg-background-100 rounded-lg group">
                  <summary className="px-6 py-4 cursor-pointer flex items-center justify-between text-foreground-950 font-medium text-sm">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-6 pb-4 text-foreground-600 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative w-full px-4 md:px-6 py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20warm%20minimalist%20workspace%20with%20fountain%20pen%20and%20cream%20paper%2C%20soft%20natural%20light%2C%20artistic%20flat%20lay%20composition%2C%20warm%20beige%20and%20ivory%20tones%2C%20editorial%20photography%20with%20soft%20shadows%2C%20no%20people&width=1600&height=700&seq=cta-bg-2&orientation=landscape"
              alt="开始起草文案"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/18 to-black/25"></div>
          </div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-background-50 mb-4">
              准备好开始起草了吗？
            </h2>
            <p className="text-background-50/80 text-base md:text-lg mb-8">
              加入 50,000+ 用户，体验高效专业的文案起草服务
            </p>
            <Link
              to="/category"
              className="inline-block px-8 py-3.5 bg-accent-500 text-background-50 rounded-md text-base font-medium hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap"
            >
              免费浏览起草指南
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}